/**
 * Outlet Form Component
 * Form content for creating or editing an outlet
 */

import React, { useEffect, useState } from 'react';
import { BusinessType } from '../../../shared/types';
import { OutletFormValues } from '../../types/outlet';
import { Button, Input, Select, Toggle } from '../common';

const OUTLET_BUSINESS_TYPES = [
  { value: BusinessType.RESTAURANT_BAR, label: 'Restaurant / Bar' },
  { value: BusinessType.RETAIL, label: 'Retail Store' },
  { value: BusinessType.OTHER, label: 'Other' },
];

interface OutletFormProps {
  initialName?: string;
  initialAddress?: string;
  initialTinNumber?: string;
  initialPhone?: string;
  initialReceiptFootnote?: string;
  initialBusinessType?: BusinessType;
  initialKotTrackingEnabled?: boolean;
  initialIsKdsEnabled?: boolean;
  onSubmit: (data: OutletFormValues) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing: boolean;
}

export const OutletForm: React.FC<OutletFormProps> = ({
  initialName = '',
  initialAddress = '',
  initialTinNumber = '',
  initialPhone = '',
  initialReceiptFootnote = '',
  initialBusinessType = BusinessType.OTHER,
  initialKotTrackingEnabled = false,
  initialIsKdsEnabled = false,
  onSubmit,
  onCancel,
  isLoading,
  isEditing,
}) => {
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState(initialAddress);
  const [tinNumber, setTinNumber] = useState(initialTinNumber);
  const [phone, setPhone] = useState(initialPhone);
  const [receiptFootnote, setReceiptFootnote] = useState(
    initialReceiptFootnote,
  );
  const [businessType, setBusinessType] =
    useState<BusinessType>(initialBusinessType);
  const [kotTrackingEnabled, setKotTrackingEnabled] = useState(
    initialKotTrackingEnabled,
  );
  const [isKdsEnabled, setIsKdsEnabled] = useState(initialIsKdsEnabled);

  useEffect(() => {
    setName(initialName);
    setAddress(initialAddress);
    setTinNumber(initialTinNumber);
    setPhone(initialPhone);
    setReceiptFootnote(initialReceiptFootnote);
    setBusinessType(initialBusinessType);
    setKotTrackingEnabled(initialKotTrackingEnabled);
    setIsKdsEnabled(initialIsKdsEnabled);
  }, [
    initialAddress,
    initialBusinessType,
    initialIsKdsEnabled,
    initialKotTrackingEnabled,
    initialName,
    initialPhone,
    initialReceiptFootnote,
    initialTinNumber,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit({
        name: name.trim(),
        address: address.trim(),
        tinNumber: tinNumber.trim(),
        phone: phone.trim(),
        receiptFootnote: receiptFootnote.trim(),
        businessType,
        kotTrackingEnabled,
        isKdsEnabled,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* eslint-disable jsx-a11y/label-has-associated-control -- inputs are nested inside labels */}
      <div className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Outlet Name *
          </span>
          <Input
            type="text"
            value={name}
            onChange={(value) => setName(value.toString())}
            placeholder="e.g., Downtown Branch"
            required
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </span>
          <Input
            type="text"
            value={address}
            onChange={(value) => setAddress(value.toString())}
            placeholder="123 Main Street, City"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">
              TIN Number
            </span>
            <Input
              type="text"
              value={tinNumber}
              onChange={(value) => setTinNumber(value.toString())}
              placeholder="e.g., 1234567890"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </span>
            <Input
              type="text"
              value={phone}
              onChange={(value) => setPhone(value.toString())}
              placeholder="e.g., +256 700 000 000"
            />
          </label>
        </div>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Receipt Footnote Message
          </span>
          <textarea
            value={receiptFootnote}
            onChange={(e) => setReceiptFootnote(e.target.value)}
            placeholder="e.g., Thank you for your business!"
            rows={2}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
          />
        </label>
        <Select
          label="Business Type"
          options={OUTLET_BUSINESS_TYPES}
          value={businessType}
          onChange={(value) => setBusinessType(value as BusinessType)}
        />
        <div>
          <Toggle
            checked={kotTrackingEnabled}
            onChange={setKotTrackingEnabled}
            label="Require physical KOT number before printing"
          />
          <p className="mt-1 text-xs text-gray-500">
            When on, staff must enter the number of the paper KOT ticket before
            a KOT confirms and prints, so managers can reconcile digital KOTs
            against physical stubs.
          </p>
        </div>
        <div>
          <Toggle
            checked={isKdsEnabled}
            onChange={setIsKdsEnabled}
            label="Enable Kitchen Display Screen"
          />
          <p className="mt-1 text-xs text-gray-500">
            Only turn this on if this outlet has a dedicated device with at
            least 8GB of RAM for the kitchen. When off, the Kitchen Display
            Screen is fully disabled for this outlet — no data is synced or
            written for it.
          </p>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Button
          onClick={onCancel}
          disabled={isLoading}
          variant="outline"
          fullWidth
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !name.trim()}
          isLoading={isLoading}
          fullWidth
        >
          {isLoading
            ? 'Saving...'
            : isEditing
              ? 'Update Outlet'
              : 'Create Outlet'}
        </Button>
      </div>
    </form>
  );
};
